// AiActionReport.js

import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Spinner,
  Select,
  Button,
  useTheme,
  useColorMode,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
  faChevronLeft,
  faChevronRight,
  faMagnifyingGlassDollar,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { cqAPI } from "../../lib/Auth";

function AiActionReport() {
  const [actionsReport, setActionsReport] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRange, setSelectedRange] = useState("Today");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });
  const [dateRange, setDateRange] = useState([
    moment().startOf("day").toDate(),
    moment().endOf("day").toDate(),
  ]);

  const [startDate, endDate] = dateRange;
  const theme = useTheme();
  const { colorMode } = useColorMode();

  const fetchActions = async (params = {}) => {
    const { startDate, endDate } = params;
    setIsLoading(true);

    try {
      const formattedStartDate = startDate ? moment(startDate).format("L") : moment(dateRange[0]).format("L");
      const formattedEndDate = endDate ? moment(endDate).format("L") : moment(dateRange[1]).format("L");

      const response = await cqAPI.get(`/reports/ai-actions?rangeBeg=${formattedStartDate}&rangeEnd=${formattedEndDate}`);
      if (response.type === "ok") {
        setActionsReport(response.data);
      }
    } catch (error) {
      console.error("Error fetching AI actions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // On mount – initialise from URL hash (shareable reports)
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const sd = hashParams.get("startDate") ? moment(hashParams.get("startDate")).toDate() : moment().startOf("day").toDate();
    const ed = hashParams.get("endDate") ? moment(hashParams.get("endDate")).toDate() : moment().endOf("day").toDate();
    const sr = hashParams.get("selectedRange") || "Today";

    setDateRange([sd, ed]);
    setSelectedRange(sr);
    fetchActions({ startDate: sd, endDate: ed });
  }, []);

  useEffect(() => {
    // keep URL up‑to‑date for shareable links
    const queryParams = new URLSearchParams({
      startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
      endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
      selectedRange,
    }).toString();
    window.location.hash = queryParams;
  }, [startDate, endDate, selectedRange]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") direction = "descending";
    setSortConfig({ key, direction });
  };

  const sortedRows = useMemo(() => {
    let sortable = [...actionsReport];
    if (sortConfig.key !== null) {
      sortable.sort((a, b) => {
        const va = sortConfig.key === "created" ? new Date(a[sortConfig.key]) : a[sortConfig.key];
        const vb = sortConfig.key === "created" ? new Date(b[sortConfig.key]) : b[sortConfig.key];
        if (va < vb) return sortConfig.direction === "ascending" ? -1 : 1;
        if (va > vb) return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [actionsReport, sortConfig]);
  const showLocationColumn = sortedRows.some((row) => row.location_name);
  const dateBasis = showLocationColumn ? "17%" : "20%";
  const patientBasis = showLocationColumn ? "17%" : "20%";
  const typeBasis = showLocationColumn ? "13%" : "15%";
  const actionsBasis = showLocationColumn ? "22%" : "25%";
  const failuresBasis = showLocationColumn ? "18%" : "20%";

  const handleDateRangeSelection = (value) => {
    setSelectedRange(value);
    let s, e;
    switch (value) {
      case "Today":
        s = moment().startOf("day");
        e = moment().endOf("day");
        break;
      case "Yesterday":
        s = moment().subtract(1, "days").startOf("day");
        e = moment().subtract(1, "days").endOf("day");
        break;
      case "Last 7 days":
        s = moment().subtract(6, "days").startOf("day");
        e = moment().endOf("day");
        break;
      case "Last 4 weeks":
        s = moment().subtract(4, "weeks").startOf("day");
        e = moment().endOf("day");
        break;
      case "Month to Date":
        s = moment().startOf("month");
        e = moment().endOf("day");
        break;
      case "Quarter to date":
        s = moment().startOf("quarter");
        e = moment().endOf("day");
        break;
      case "Year to date":
        s = moment().startOf("year");
        e = moment().endOf("day");
        break;
      case "Custom":
      default:
        s = null;
        e = null;
    }
    setDateRange([s ? s.toDate() : null, e ? e.toDate() : null]);
  };

  const renderCustomHeader = ({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
    <Flex direction="column" width="100%" paddingX="20px">
      <Flex justifyContent="center" alignItems="center" mt={4}>
        <Box p={2} borderRadius="md" borderWidth="2px" borderColor="purple.500" mr={2}>
          {startDate ? startDate.toLocaleDateString() : "Start Date"}
        </Box>
        <Text mx={2}>→</Text>
        <Box p={2} borderRadius="md" borderWidth="2px" borderColor="purple.500">
          {endDate ? endDate.toLocaleDateString() : "End Date"}
        </Box>
      </Flex>
      <Flex justifyContent="space-between" alignItems="center" pt={2}>
        <Box as="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </Box>
        <Box>
          {date.toLocaleString("default", { month: "long", year: "numeric" })}
        </Box>
        <Box as="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
          <FontAwesomeIcon icon={faChevronRight} />
        </Box>
      </Flex>
    </Flex>
  );

  const hoverBgColor = colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];

  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh" width="100%" position="relative">
        <Spinner color="blue.500" size="xl" />
      </Flex>
    );
  }

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%">
      {/* Control Row */}
      <Flex mb={4} align="center">
        {/* Date Range Quick Select */}
        <Select
          placeholder="Select range"
          value={selectedRange}
          onChange={(e) => handleDateRangeSelection(e.target.value)}
          width="200px"
          mr={2}
        >
          <option value="Today">Today</option>
          <option value="Yesterday">Yesterday</option>
          <option value="Last 7 days">Last 7 days</option>
          <option value="Last 4 weeks">Last 4 weeks</option>
          <option value="Month to Date">Month to Date</option>
          <option value="Quarter to date">Quarter to date</option>
          <option value="Year to date">Year to date</option>
          <option value="Custom">Custom</option>
        </Select>
        {/* Date Picker */}
        <DatePicker
          className={`custom-datepicker ${colorMode === "light" ? "light-theme" : "dark-theme"}`}
          selectsRange
          startDate={startDate}
          endDate={endDate}
          onChange={(update) => {
            setDateRange(update);
            setSelectedRange("Custom");
          }}
          isClearable
          renderCustomHeader={renderCustomHeader}
        />
        {/* Spacer to push Show Actions to the far right */}
        <Box ml="auto">
          <Button leftIcon={<FontAwesomeIcon icon={faMagnifyingGlassDollar} />} onClick={fetchActions}>
            Show Actions
          </Button>
        </Box>
      </Flex>

      <Flex data-report-download-target justify="flex-end" mb={2} className="no-print" />

      {/* Report Table */}
      <VStack spacing={0} align="stretch">
        {/* Header Row */}
        <Flex borderBottom="1px" borderColor="gray.200" p={2} fontWeight="bold">
          <HStack spacing={8} flex="1">
            <Text flexBasis={dateBasis} cursor="pointer" onClick={() => requestSort("created")}>Date</Text>
            {showLocationColumn && <Text flexBasis="13%">Location</Text>}
            <Text flexBasis={patientBasis} cursor="pointer" onClick={() => requestSort("patient")}>Patient</Text>
            <Text flexBasis={typeBasis} cursor="pointer" onClick={() => requestSort("type")}>Session Type</Text>
            <Text flexBasis={actionsBasis}>Actions</Text>
            <Text flexBasis={failuresBasis}>Failure Reason(s)</Text>
          </HStack>
        </Flex>

        {/* Data Rows */}
        {sortedRows.map((session) => {
          const actionSummary = session.actions
            .map((a) => `${a.name} ${a.completed ? "✓" : "✕"}`)
            .join(", ");
          const failures = session.actions
            .filter((a) => !a.completed && a.failure_reason)
            .map((a) => a.failure_reason)
            .join("; ");

          return (
            <Flex
              key={session._id}
              p={2}
              _hover={{ backgroundColor: hoverBgColor }}
            >
              <HStack spacing={8} flex="1">
                <Text flexBasis={dateBasis} textAlign="left">
                  {moment(session.created).format("MMM D, YYYY h:mm a")}
                </Text>
                {showLocationColumn && (
                  <Text flexBasis="13%" textAlign="left">
                    {session.location_name || "-"}
                  </Text>
                )}
                <Text flexBasis={patientBasis} textAlign="left">
                  {session.patient}
                </Text>
                <Text flexBasis={typeBasis} textAlign="left">
                  {session.type}
                </Text>
                <Text flexBasis={actionsBasis} textAlign="left">
                  {actionSummary}
                </Text>
                <Text flexBasis={failuresBasis} textAlign="left" whiteSpace="pre-wrap">
                  {failures}
                </Text>
              </HStack>
            </Flex>
          );
        })}
      </VStack>
    </Box>
  );
}

export default AiActionReport;
